/**
 * Wall Breaker (slingshot) API helpers. Finite campaign flow: start today's session, then report each
 * cleared level. The server owns the economy — coins depend only on the level index + per-day paid set +
 * cap, never on a client-supplied amount. ballsLeft/stars are advisory (analytics/UI) only.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { SLINGSHOT_GAME_KEY } from '../constants/slingshot.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface SlingshotStart {
  levelCount: number;
  ballsPerLevel: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestLevel: number;
  paidLevels: number[];
}

export interface SlingshotClearResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestLevel: number;
  newBest: boolean;
}

export async function startSlingshotRun(apiClient: ApiClient): Promise<SlingshotStart> {
  const response = await apiClient<{ success: boolean; data: SlingshotStart; message?: string }>(
    API_ENDPOINTS.games.levelStart(SLINGSHOT_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function recordSlingshotLevelClear(
  apiClient: ApiClient,
  args: { level: number; ballsLeft: number; stars: 1 | 2 | 3 },
): Promise<SlingshotClearResult> {
  const response = await apiClient<{ success: boolean; data: SlingshotClearResult; message?: string }>(
    API_ENDPOINTS.games.levelClear(SLINGSHOT_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the level');
  return payload.data;
}
